import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { umschlag, type Umschlag } from "../plattform/api";
import { vibrieren } from "../plattform/geraet";
import { serverkontakt } from "../plattform/serverzeit";
import { beobachten, eintraege, einreihen, type Uebertragungsstand } from "../plattform/warteschlange";
import { Kachel } from "./Kachel";
import { Symbol } from "./Symbol";

/**
 * Rückgängig statt Rückfrage (ADR-0023, Anforderungen K10): Eine Meldung bekommt beim Erfassen ihre Meldungs-Id, wird
 * bestätigt und bleibt 10 Sekunden zurücknehmbar (Token `dauer.rueckgaengig`); danach geht sie in die Warteschlange
 * (ADR-0020). Bestätigung und Leiste erscheinen im selben Bild (design/README §6, §9.3, §9.4) und bleiben nach Ablauf bis
 * zur nächsten Berührung stehen.
 *
 * Die vorgemerkte Meldung gehört dem Gerät, nicht einer Ansicht: Ein Ablauf (z. B. „Schuss“, oberflaeche.md §5) merkt
 * sie vor und kehrt zur Startansicht zurück, die Bestätigung und Leiste zeigt; ein Ansichtswechsel nimmt sie weder
 * zurück noch verliert er sie. Es gibt höchstens eine offene Vormerkung; eine neue schließt die vorige sofort ab.
 */
/** Eine Angabe der Bestätigung: Symbol (design/icons) und Text. */
export type Angabe = readonly [symbol: string, text: string];

export interface Vormerkung {
  befehl: string;
  /** Begriff der Hauptkachel, z. B. „Schuss“ */
  begriff: string;
  symbol: string;
  /** Angaben unter dem Begriff je mit ihrem Symbol, z. B. Rehwild, Stück liegt (Referenz `#s-bestaetigung`) */
  angaben: readonly Angabe[];
  /** Titel in der Warteschlange (oberflaeche.md §1.5) */
  titel: (u: Umschlag) => string;
  daten: Record<string, unknown>;
  /**
   * Weitere Befehle derselben Meldung (z. B. „in Ordnung“ der Nachweiskontrolle: Jagdschein und Schießnachweis), je mit
   * eigener Meldungs-Id beim Erfassen; sie gehen mit ihr zusammen in die Warteschlange oder werden mit ihr zurückgenommen.
   */
  weitere?: Array<{ befehl: string; titel: string; daten: Record<string, unknown> }>;
}

interface Offen extends Vormerkung {
  umschlag: Umschlag;
  weitereUmschlaege: Array<{ befehl: string; titel: string; umschlag: Umschlag }>;
  bis: number;
  /**
   * Das Fenster ist abgelaufen, die Meldung liegt in der Warteschlange. Bestätigung und Leiste bleiben bis zur nächsten
   * Berührung stehen (design/README §6, §9.4, A-01 … A-04: nichts springt unter dem Daumen), ohne „Rückgängig“.
   */
  abgelaufen: boolean;
}

/** `true`, solange die Meldung `meldungsId` noch zurückgenommen werden kann. */
export function fensterOffen(o: Offen | null, meldungsId: string | null): boolean {
  return !!o && !o.abgelaufen && o.umschlag.meldungsId === meldungsId;
}

/** Dauer des Fensters aus dem Token; der Build kürzt `10000ms` zu `10s`, daher beide Einheiten. */
function dauerMs(): number {
  const wert = getComputedStyle(document.documentElement).getPropertyValue("--dj-bewegung-dauer-rueckgaengig").trim();
  const m = /^(\d+(?:\.\d+)?)(ms|s)$/.exec(wert);
  return m ? Number(m[1]) * (m[2] === "s" ? 1000 : 1) : 10_000;
}

let offen: Offen | null = null;
let zeitgeber: number | null = null;
let beruehrungAbmelden: (() => void) | null = null;
const hoerer = new Set<() => void>();

function setzen(o: Offen | null): void {
  if (beruehrungAbmelden && !o?.abgelaufen) {
    beruehrungAbmelden();
    beruehrungAbmelden = null;
  }
  offen = o;
  for (const h of hoerer) h();
}

async function abschliessen(o: Offen): Promise<void> {
  await einreihen(o.befehl, o.titel(o.umschlag), o.umschlag);
  for (const w of o.weitereUmschlaege) await einreihen(w.befehl, w.titel, w.umschlag);
}

/** Bedienelemente, die bei einer Berührung selbst wirken (Notruf, Statusleiste, Hauptaktion, Listenknöpfe). */
const BEDIENELEMENT = "button, a[href], input, select, textarea, [role=button], [role=link], [role=switch], [role=checkbox], [role=radio], [role=tab]";

/**
 * Nach Ablauf des Fensters: Die nächste Berührung beendet Bestätigung und Leiste. Trifft sie ein Bedienelement, wirkt es
 * und die Leiste endet erst nach dem Loslassen (kein Wechsel unter dem Finger); jede andere Berührung wird verworfen
 * (sie löst nichts aus, design/README §6 A-f1/A-f2). Eine Taste wirkt ebenso.
 */
function bisZurBeruehrung(meldungsId: string): () => void {
  const beenden = () => {
    if (offen?.abgelaufen && offen.umschlag.meldungsId === meldungsId) setzen(null);
  };
  const nachDemLoslassen = () => window.setTimeout(beenden, 0);
  const unten = (e: PointerEvent) => {
    const ziel = e.target instanceof Element ? e.target.closest(BEDIENELEMENT) : null;
    if (ziel && !ziel.closest("[inert]")) {
      window.addEventListener("pointerup", nachDemLoslassen, { capture: true, once: true });
      window.addEventListener("pointercancel", nachDemLoslassen, { capture: true, once: true });
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    beenden();
  };
  const taste = () => nachDemLoslassen();
  window.addEventListener("pointerdown", unten, true);
  window.addEventListener("keydown", taste, true);
  return () => {
    window.removeEventListener("pointerdown", unten, true);
    window.removeEventListener("keydown", taste, true);
  };
}

function ablaufen(o: Offen): void {
  zeitgeber = null;
  if (offen?.umschlag.meldungsId === o.umschlag.meldungsId && !offen.abgelaufen) {
    setzen({ ...offen, abgelaufen: true });
    beruehrungAbmelden = bisZurBeruehrung(o.umschlag.meldungsId);
  }
  void abschliessen(o);
}

function vormerken(v: Vormerkung): Umschlag {
  if (offen && !offen.abgelaufen) {
    if (zeitgeber) window.clearTimeout(zeitgeber);
    zeitgeber = null;
    void abschliessen(offen);
  }
  const o: Offen = {
    ...v, umschlag: umschlag(v.daten), weitereUmschlaege: (v.weitere ?? []).map((w) => ({ befehl: w.befehl, titel: w.titel, umschlag: umschlag(w.daten) })),
    bis: Date.now() + dauerMs(), abgelaufen: false,
  };
  vibrieren("meldung");
  setzen(o);
  zeitgeber = window.setTimeout(() => ablaufen(o), dauerMs());
  return o.umschlag;
}

/**
 * Ergänzt die noch offene Vormerkung (z. B. die Fluchtrichtung nach „Anschuss“ oder eine Geräteposition, die erst nach
 * dem Erfassen eintrifft). `false`, wenn das Fenster schon abgelaufen oder die Meldung zurückgenommen ist.
 */
function ergaenzen(meldungsId: string, daten: Record<string, unknown>, angaben?: readonly Angabe[]): boolean {
  if (!offen || !fensterOffen(offen, meldungsId)) return false;
  const neu = { ...offen.umschlag.daten, ...daten };
  offen.umschlag.daten = neu;
  setzen({ ...offen, daten: neu, umschlag: offen.umschlag, ...(angaben ? { angaben } : {}) });
  return true;
}

function zuruecknehmen(): void {
  if (!offen || offen.abgelaufen) return;
  if (zeitgeber) window.clearTimeout(zeitgeber);
  zeitgeber = null;
  vibrieren("rueckgaengig");
  setzen(null);
}

export function useRueckgaengig() {
  const aktuell = useSyncExternalStore((f) => { hoerer.add(f); return () => hoerer.delete(f); }, () => offen);
  return { offen: aktuell, vormerken, ergaenzen, zuruecknehmen };
}

/**
 * Rückgängig-Leiste (design/README §6, §9.4; oberflaeche.md §5): `region` „Rückgängig“ mit Begriff, `status` „noch <n> s“,
 * Restzeit als Balken (`transform: scaleX`, eine Zeitquelle; bei reduzierter Bewegung in Sekundenschritten) und
 * `button` „Rückgängig“ (invertiert, sichtbare Zielgrenze). Nach Ablauf steht die Leiste bis zur nächsten Berührung mit
 * dem Übertragungsstand der Meldung (oberflaeche.md §1.5) an Stelle der Restzeit; der Knopf behält seinen Platz
 * unsichtbar, damit sich nichts verschiebt.
 */
export function RueckgaengigLeiste({ offen, onRueckgaengig }: { offen: Offen; onRueckgaengig: () => void }) {
  const [rest, setRest] = useState(() => Math.max(0, offen.bis - Date.now()));
  const balken = useRef<HTMLElement>(null);
  const stand = useUebertragungsstand(offen.abgelaufen ? offen.umschlag.meldungsId : null);
  useEffect(() => {
    let rahmen = 0;
    const gesamt = offen.bis - Date.now();
    const reduziert = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const schritt = () => {
      const r = Math.max(0, offen.bis - Date.now());
      setRest(r);
      const anteil = gesamt > 0 ? (reduziert ? Math.ceil(r / 1000) / Math.ceil(gesamt / 1000) : r / gesamt) : 0;
      if (balken.current) balken.current.style.transform = `scaleX(${anteil})`;
      if (r > 0) rahmen = requestAnimationFrame(schritt);
    };
    rahmen = requestAnimationFrame(schritt);
    return () => cancelAnimationFrame(rahmen);
  }, [offen.bis]);
  return (
    <section className={`rg${offen.abgelaufen ? " rg--abgelaufen" : ""}`} role="region" aria-label="Rückgängig">
      <div className="rg__text">
        <b>{offen.begriff}</b>
        <span className="rg__s" role="status">{offen.abgelaufen ? stand : `noch ${Math.ceil(rest / 1000)} s`}</span>
        <span className="rg__zeit" aria-hidden="true"><i ref={balken} /></span>
      </div>
      <Kachel symbol="rueckgaengig" begriff="Rückgängig" art="kompakt" className="k--rg" onPress={onRueckgaengig} />
    </section>
  );
}

/** Übertragungsstand einer Meldung aus der Warteschlange des Geräts; bis sie dort steht „noch nicht übertragen“. */
function useUebertragungsstand(meldungsId: string | null): Uebertragungsstand {
  const [stand, setStand] = useState<Uebertragungsstand>("noch nicht übertragen");
  useEffect(() => {
    if (!meldungsId) return;
    let aktiv = true;
    const lesen = () => void eintraege().then((alle) => {
      const e = alle.find((x) => x.meldungsId === meldungsId);
      if (aktiv && e) setStand(e.stand);
    });
    lesen();
    const abmelden = beobachten(lesen);
    return () => { aktiv = false; abmelden(); };
  }, [meldungsId]);
  return stand;
}

/**
 * Bestätigung nach einer Meldung (design/README §9.3; oberflaeche.md §5): `status` auf invertierter Fläche über dem
 * Hauptbereich mit Haken, dem Begriff der Hauptkachel, den Angaben mit ihren Symbolen und Stand bzw. Uhrzeit.
 */
export function Bestaetigung({ offen, kontext }: { offen: Offen; kontext?: string }) {
  // Uhrzeit nach Serverzeit wie im Zustandsband (ADR-0030): Gerätezeit der Meldung plus die bekannte Abweichung
  const zeit = berlinUhrzeit(new Date(new Date(offen.umschlag.geraetezeit).getTime() + serverkontakt().abweichung));
  return (
    <div className="best" role="status">
      <Symbol name="haken" className="best__haken" />
      <b className="best__b">{offen.begriff}</b>
      {offen.angaben.length ? (
        <div className="best__angaben">
          {offen.angaben.map(([symbol, text]) => (
            // der Pfeil der Flucht- bzw. Bewegungsrichtung zeigt in die gewählte Richtung (wie die Kacheln des 3 × 3)
            <span key={text} className={symbol === "fluchtrichtung" ? `best__a k--richtung-${text}` : "best__a"}><Symbol name={symbol} /><span>{text}</span></span>
          ))}
        </div>
      ) : null}
      <span className="best__m">{[kontext, zeit].filter(Boolean).join(" · ")}</span>
    </div>
  );
}
